import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { Upload, CheckCircle2, Clock, FileArchive, X, Pencil, Plus, FileSpreadsheet, Trash2, AlertTriangle, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/** Admin — Products manager with drag-and-drop image upload.
 *  Lists every SW-* SKU, tags Coming Soon products in red and lets admin drop
 *  or pick a real Samara photograph to instantly flip the SKU to available.
 *  Powered by POST /api/admin/products/{id}/upload-image.
 */
export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // product being edited, or "new"
  const [deleting, setDeleting] = useState(null); // product pending photo delete
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => api.get("/products?limit=200")
    .then((r) => setItems((r.data.items || []).sort((a, b) =>
      a.is_coming_soon === b.is_coming_soon ? 0 : a.is_coming_soon ? -1 : 1)));
  useEffect(() => { load(); }, []);

  // Canonical categories — SAME 6 labels as public storefront tabs. Hardcoded
  // (not derived from data) so Fashion & Agriculture appear even before any
  // product exists in those buckets yet — critical for a fresh production DB.
  const CANON_CATS = [
    { v: "health",        l: "Health & Wellness" },
    { v: "personal_care", l: "Personal Care" },
    { v: "combo",         l: "Combos" },
    { v: "fashion",       l: "Fashion & Apparel" },
    { v: "agriculture",   l: "Agriculture" },
    { v: "education",     l: "Education" },
  ];
  // Merge any legacy categories present in DB but not in the canonical list
  // (e.g. old "beauty" / "nutrition") so admins can still filter them.
  const dbCats = Array.from(new Set(items.map((p) => p.category).filter(Boolean)));
  const extraCats = dbCats
    .filter((c) => !CANON_CATS.find((x) => x.v === c))
    .map((c) => ({ v: c, l: c.replace(/_/g, " ") + " (legacy)" }));
  const categoryOptions = [...CANON_CATS, ...extraCats];

  const visible = items.filter((p) => {
    if (filter === "coming" && !p.is_coming_soon) return false;
    if (filter === "live" && p.is_coming_soon) return false;
    if (cat !== "all" && p.category !== cat) return false;
    const term = q.trim().toLowerCase();
    if (!term) return true;
    const hay = [p.name, p.sku, p.description, p.category].filter(Boolean).join(" ").toLowerCase();
    return term.split(/\s+/).every((t) => hay.includes(t));
  });
  const csCount = items.filter((p) => p.is_coming_soon).length;
  const liveCount = items.length - csCount;

  const upload = async (product, file) => {
    if (!file || !file.type?.startsWith("image/")) return toast.error("Choose an image (JPG or PNG)");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image too large — 5 MB max");
    setBusy(product.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/products/${product.id}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${product.name} is now live`);
      setItems((prev) => prev.map((p) => p.id === product.id
        ? { ...p, image: data.image, is_coming_soon: false, updated_at: Date.now().toString() } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(null); }
  };

  const removeImage = async (product) => {
    // Opens the red warning dialog. Actual delete runs from confirmDelete().
    setDeleting(product);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const product = deleting;
    setDeleteBusy(true);
    setBusy(product.id);
    try {
      const { data } = await api.delete(`/admin/products/${product.id}/image`);
      toast.success(`Photo removed — ${product.name} is back to Coming Soon`);
      setItems((prev) => prev.map((p) => p.id === product.id
        ? { ...p, image: data.image, is_coming_soon: true } : p));
      setDeleting(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteBusy(false);
      setBusy(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Admin · Product Manager</div>
            <h1 className="font-serif text-3xl text-navy mt-1">Product Photos</h1>
            <p className="text-sm text-navy/60 mt-1">Drop a photograph on any tile to flip it live. Tap <b>EDIT</b> to change name, price, stock or category.</p>
          </div>
          <div className="flex gap-1.5 flex-wrap items-center" data-testid="admin-products-filter">
            <button
              type="button"
              onClick={() => setEditing("new")}
              data-testid="admin-new-product"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 bg-royal text-white hover:bg-royal-deep rounded-sm"
            >
              <Plus size={11} /> New Product
            </button>
            <ExcelUploader onDone={load} />
            {[
              { k: "all", l: `All (${items.length})` },
              { k: "coming", l: `Coming Soon (${csCount})` },
              { k: "live", l: `Live (${liveCount})` },
            ].map((t) => (
              <button key={t.k} data-testid={`filter-${t.k}`} onClick={() => setFilter(t.k)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm border transition-colors ${
                  filter === t.k ? "bg-navy text-white border-navy" : "bg-white text-navy/60 border-line hover:border-gold"
                }`}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* Search + Category filter */}
        <div className="bg-white border border-border rounded-sm p-3 mb-4 flex flex-col md:flex-row gap-2.5" data-testid="admin-products-search-bar">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, SKU, description…"
              data-testid="admin-products-search"
              className="w-full pl-9 pr-9 py-2 text-sm border border-line rounded-sm focus:border-gold outline-none"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                data-testid="admin-products-search-clear"
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-navy/40 hover:text-rose"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            data-testid="admin-products-category"
            className="border border-line px-3 py-2 text-sm rounded-sm focus:border-gold outline-none min-w-[200px]"
          >
            <option value="all">All Categories</option>
            {categoryOptions.map((c) => (
              <option key={c.v} value={c.v}>{c.l}</option>
            ))}
          </select>
          <div className="text-[11px] uppercase tracking-widest text-navy/50 self-center px-2 whitespace-nowrap" data-testid="admin-products-count">
            {visible.length} of {items.length}
          </div>
        </div>

        <BulkZipUploader onDone={load} />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {visible.map((p) => <ProductTile key={p.id} product={p} onFile={(f) => upload(p, f)} onEdit={() => setEditing(p)} onDeleteImage={() => removeImage(p)} busy={busy === p.id} />)}
          {visible.length === 0 && (
            <div className="col-span-full py-12 text-center text-sm text-navy/50">
              {q || cat !== "all" || filter !== "all"
                ? "No products match your search / filters."
                : "Nothing here yet."}
              {(q || cat !== "all") && (
                <button
                  type="button"
                  onClick={() => { setQ(""); setCat("all"); }}
                  data-testid="admin-products-clear-filters"
                  className="block mx-auto mt-2 text-[11px] uppercase tracking-widest text-royal hover:text-gold"
                >
                  Clear filters →
                </button>
              )}
            </div>
          )}
        </div>

        {editing && (
          <ProductEditor
            product={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        <Dialog open={!!deleting} onOpenChange={(v) => { if (!deleteBusy && !v) setDeleting(null); }}>
          <DialogContent data-testid="delete-warning-dialog" className="sm:max-w-lg border-rose/40">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 grid place-items-center bg-rose/10 border border-rose/30 rounded-sm shrink-0">
                  <AlertTriangle size={22} className="text-rose" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-rose">Warning · Photo delete</div>
                  <DialogTitle className="font-serif text-xl text-navy">Remove this product photo?</DialogTitle>
                </div>
              </div>
              <DialogDescription className="text-navy/70 leading-relaxed pt-3 space-y-2">
                {deleting && (
                  <span className="block bg-cream/60 border border-line rounded-sm px-4 py-3 text-sm">
                    <b className="text-navy">{deleting.name}</b>
                    <span className="text-navy/50 font-mono ml-2 text-xs">{deleting.sku}</span>
                    <div className="text-[11px] text-navy/50 mt-1">
                      MRP ₹{Number(deleting.mrp || 0).toLocaleString("en-IN")} · SP {deleting.sp || deleting.sp || 0}
                    </div>
                  </span>
                )}
                <span className="block text-sm">
                  This will <b className="text-rose">clear the product image</b> and revert this SKU to the
                  <b> Coming Soon</b> placeholder across the storefront, cart and IBA shop.
                </span>
                <span className="block text-[12.5px] text-navy/60 leading-relaxed">
                  Product details (name, price, stock) will <b>not</b> be deleted — only the photo is removed.
                  You can upload a new image later to flip it live again.
                </span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4 gap-2 sm:gap-2">
              <button
                type="button"
                onClick={() => setDeleting(null)}
                disabled={deleteBusy}
                data-testid="delete-warning-cancel"
                className="px-4 py-2 text-[11px] uppercase tracking-widest border border-border rounded-sm text-navy/70 hover:bg-navy/5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteBusy}
                data-testid="delete-warning-confirm"
                className="px-4 py-2 text-[11px] uppercase tracking-widest rounded-sm text-white bg-rose hover:bg-rose/90 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                <Trash2 size={12} /> {deleteBusy ? "Removing…" : "Yes, remove photo"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function ProductTile({ product, onFile, onEdit, onDeleteImage, busy }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  return (
    <div
      data-testid={`admin-product-${product.id}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      className={`border rounded-sm bg-white overflow-hidden transition-all ${
        dragging ? "border-gold ring-4 ring-gold/20" : "border-line hover:border-gold/40"
      }`}
    >
      <div className={`relative aspect-square bg-ivory ${busy ? "opacity-60" : ""}`}>
        <img
          src={product.image ? `${product.image}${product.image.includes("?") ? "&" : "?"}v=${product.updated_at || product.id}` : "/product-images/coming-soon.svg"}
          alt={product.name}
          onError={(e) => { e.currentTarget.src = "/product-images/coming-soon.svg"; }}
          className={`w-full h-full object-cover ${product.is_coming_soon ? "opacity-70 grayscale" : ""}`} />
        {product.is_coming_soon ? (
          <span className="absolute top-2 left-2 bg-rose text-white text-[9px] uppercase tracking-widest px-2 py-0.5">
            <Clock size={9} className="inline -mt-0.5 mr-0.5" /> Coming Soon
          </span>
        ) : (
          <span className="absolute top-2 left-2 bg-olive text-white text-[9px] uppercase tracking-widest px-2 py-0.5">
            <CheckCircle2 size={9} className="inline -mt-0.5 mr-0.5" /> Live
          </span>
        )}
        {busy && <div className="absolute inset-0 grid place-items-center bg-white/70 text-xs text-navy">Uploading…</div>}
      </div>
      <div className="p-3">
        <div className="text-[9px] uppercase tracking-widest text-navy/40">{product.sku}</div>
        <div className="font-serif text-sm text-navy line-clamp-1 mt-0.5">{product.name}</div>
        <div className="text-[10px] text-navy/50 mt-0.5">SP {product.sp || product.sp || 0} · MRP ₹{Number(product.mrp || 0).toLocaleString("en-IN")}</div>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          <button type="button" data-testid={`upload-btn-${product.id}`} onClick={() => inputRef.current?.click()} disabled={busy}
            className="text-[11px] uppercase tracking-widest border border-navy/20 hover:border-gold hover:text-gold-deep px-2 py-1.5 rounded-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
            <Upload size={11} /> Photo
          </button>
          <button type="button" data-testid={`edit-btn-${product.id}`} onClick={onEdit}
            title="Edit name, price, stock, category & description"
            className="text-[11px] uppercase tracking-widest bg-royal text-white hover:bg-royal-deep px-2 py-1.5 rounded-sm inline-flex items-center justify-center gap-1.5">
            <Pencil size={11} /> Edit
          </button>
          <button type="button" data-testid={`delete-btn-${product.id}`} onClick={onDeleteImage} disabled={busy || product.is_coming_soon}
            title={product.is_coming_soon ? "Already Coming Soon" : "Delete photo & mark Coming Soon"}
            className="text-[11px] uppercase tracking-widest border border-rose/40 text-rose hover:bg-rose hover:text-white px-2 py-1.5 rounded-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-rose">
            <Trash2 size={11} /> Delete
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      </div>
    </div>
  );
}

/** Full editor modal — create OR edit every product field. Saves via
 *  POST /admin/products (create) or PUT /admin/products/{id} (edit). */
function ProductEditor({ product, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState(() => ({
    sku: product?.sku || "",
    name: product?.name || "",
    description: product?.description || "",
    category: product?.category || "health",
    mrp: product?.mrp ?? 0,
    dp: product?.dp ?? 0,
    sp: product?.sp ?? product?.sp ?? 0,
    bv: product?.sp ?? product?.sp ?? 0,
    stock: product?.stock ?? 0,
    tax_pct: product?.tax_pct ?? 5,
    image: product?.image || "",
    is_active: product?.is_active !== false,
    is_featured: !!product?.is_featured,
    is_coming_soon: !!product?.is_coming_soon,
    sort_order: product?.sort_order ?? 999,
  }));
  const [busy, setBusy] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImg = async (file) => {
    if (!file?.type?.startsWith("image/")) return toast.error("Choose an image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image > 5 MB");
    setUploadingImg(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      // If editing an existing product, use its endpoint (also flips it live)
      const url = product ? `/admin/products/${product.id}/upload-image` : `/admin/products/upload-image-new`;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.image);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploadingImg(false); }
  };

  const save = async () => {
    for (const req of ["sku", "name", "category"]) {
      if (!String(form[req] || "").trim()) return toast.error(`${req.toUpperCase()} is required`);
    }
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/admin/products", form);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${product.id}`, form);
        toast.success("Saved");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm"
         onClick={() => !busy && onClose()} data-testid="product-editor-modal">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-sm border border-border shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-royal">{isNew ? "Create" : "Edit"}</div>
            <h3 className="font-serif text-xl text-navy">{isNew ? "New Product" : form.name}</h3>
          </div>
          <button onClick={onClose} className="text-navy/50 hover:text-navy p-1.5" data-testid="editor-close"><X size={18} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          {/* Image preview + upload */}
          <div className="sm:col-span-2 flex items-start gap-4">
            <div className="w-32 h-32 shrink-0 border border-line rounded-sm bg-ivory overflow-hidden grid place-items-center">
              {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-navy/40">No image</span>}
            </div>
            <div className="flex-1">
              <label className="block text-[10px] uppercase tracking-widest text-navy/50">Product Image</label>
              <input type="file" accept="image/*" data-testid="editor-image-file"
                onChange={(e) => e.target.files?.[0] && uploadImg(e.target.files[0])}
                className="mt-1 text-xs" />
              <input type="text" value={form.image} onChange={(e) => set("image", e.target.value)}
                data-testid="editor-image-url"
                placeholder="Or paste an image URL"
                className="mt-2 w-full border border-line rounded-sm px-2 py-1.5 text-xs" />
              {uploadingImg && <div className="text-[11px] text-royal mt-1">Uploading…</div>}
            </div>
          </div>
          <Field label="SKU *" tid="editor-sku" val={form.sku} onChange={(v) => set("sku", v)} />
          <Field label="Name *" tid="editor-name" val={form.name} onChange={(v) => set("name", v)} />
          <Field label="Category *" tid="editor-category" val={form.category} onChange={(v) => set("category", v)}
                 options={[
                   { v: "health",        l: "Health & Wellness" },
                   { v: "personal_care", l: "Personal Care" },
                   { v: "combo",         l: "Combos" },
                   { v: "fashion",       l: "Fashion & Apparel" },
                   { v: "agriculture",   l: "Agriculture" },
                   { v: "education",     l: "Education" },
                 ]} />
          <Field label="Sort order" tid="editor-sort" type="number" val={form.sort_order} onChange={(v) => set("sort_order", +v)} />
          <Field label="MRP ₹" tid="editor-mrp" type="number" val={form.mrp} onChange={(v) => set("mrp", +v)} />
          <Field label="DP ₹ (Distributor)" tid="editor-dp" type="number" val={form.dp} onChange={(v) => set("dp", +v)} />
          <Field label="SP (Sales Point)" tid="editor-sp" type="number" val={form.sp} onChange={(v) => set("sp", +v)} />
          <Field label="SP (Sales Point)" tid="editor-bv" type="number" val={form.sp} onChange={(v) => set("bv", +v)} />
          <Field label="Stock" tid="editor-stock" type="number" val={form.stock} onChange={(v) => set("stock", +v)} />
          <Field label="Tax %" tid="editor-tax" type="number" val={form.tax_pct} onChange={(v) => set("tax_pct", +v)} />
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-navy/50">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
              data-testid="editor-description"
              className="mt-1 w-full border border-line rounded-sm px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2 flex flex-wrap gap-4">
            <Toggle label="Active (visible)"       tid="editor-active"   val={form.is_active}      onChange={(v) => set("is_active", v)} />
            <Toggle label="Featured"               tid="editor-featured" val={form.is_featured}    onChange={(v) => set("is_featured", v)} />
            <Toggle label="Coming Soon (badge)"    tid="editor-coming"   val={form.is_coming_soon} onChange={(v) => set("is_coming_soon", v)} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={busy} className="text-sm text-navy/60 hover:text-navy px-3 py-2">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="editor-save"
            className="text-sm bg-royal text-white hover:bg-royal-deep px-5 py-2 rounded-sm disabled:opacity-50">
            {busy ? "Saving…" : isNew ? "Create product" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, val, onChange, type = "text", tid, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-navy/50">{label}</span>
      {options ? (
        <select value={val} onChange={(e) => onChange(e.target.value)} data-testid={tid}
          className="mt-1 w-full border border-line rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none">
          {options.map((o) => (typeof o === "string"
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </select>
      ) : (
        <input type={type} value={val ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}
          className="mt-1 w-full border border-line rounded-sm px-2 py-1.5 text-sm focus:border-royal outline-none" />
      )}
    </label>
  );
}
function Toggle({ label, val, onChange, tid }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
      <input type="checkbox" checked={!!val} onChange={(e) => onChange(e.target.checked)} data-testid={tid} />
      <span className="text-navy/70">{label}</span>
    </label>
  );
}

/** Simple Excel bulk upload — hits POST /admin/products/bulk-excel. */
function ExcelUploader({ onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) return toast.error("Choose an .xlsx file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/products/bulk-excel", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Excel imported — ${data.created ?? 0} new, ${data.updated ?? 0} updated`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel upload failed");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => !busy && inputRef.current?.click()} disabled={busy}
        data-testid="excel-upload-btn"
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-olive/40 text-olive hover:bg-olive/5 rounded-sm disabled:opacity-50">
        <FileSpreadsheet size={11} /> {busy ? "Importing…" : "Excel Upload"}
      </button>
      <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden
        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
    </>
  );
}

/** Drop a single .zip containing images named after SKUs (or product slug).
 *  Server matches each file to a product and flips it live. Shows a per-file
 *  report so the admin can rename/fix mismatches and retry. */
function BulkZipUploader({ onDone }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".zip")) { toast.error("Choose a .zip archive"); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error("ZIP too large — 50MB max"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/upload-zip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.matched_count} product photo${data.matched_count === 1 ? "" : "s"} attached`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ZIP upload failed");
    } finally { setBusy(null); setBusy(false); }
  };

  return (
    <section data-testid="bulk-zip-uploader" className="mb-6 bg-white border border-line rounded-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-line flex items-center gap-3">
        <div className="w-9 h-9 grid place-items-center bg-royal/10 border border-royal/20 rounded-sm">
          <FileArchive size={16} className="text-royal" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-lg text-navy">Bulk ZIP Upload</h3>
          <p className="text-[11.5px] text-navy/60 -mt-0.5">
            Drop a <code>.zip</code> of images named after SKU (e.g. <code>SW-NON-500.png</code>) or the slug of the product name (e.g. <code>noni-juice.png</code>). Matches flip live instantly.
          </p>
        </div>
      </div>
      <div className="p-5">
        <div
          data-testid="bulk-zip-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
          onClick={() => !busy && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          className={`cursor-pointer border-2 border-dashed rounded-sm px-6 py-8 text-center transition-all ${
            busy ? "opacity-60" : dragging ? "border-gold bg-gold/5" : "border-line hover:border-royal/40 hover:bg-ivory"
          }`}
        >
          <Upload size={22} className="mx-auto text-royal" />
          <div className="mt-2 font-serif text-navy text-base">
            {busy ? "Uploading & matching…" : dragging ? "Drop the ZIP to upload" : "Drop a ZIP here or click to browse"}
          </div>
          <div className="text-[11px] text-navy/50 mt-1">.zip · up to 50MB · images: PNG, JPG, JPEG, WEBP · 5MB each</div>
          <input ref={inputRef} type="file" accept=".zip,application/zip" hidden
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="bulk-zip-input" />
        </div>

        {result && (
          <div className="mt-5" data-testid="bulk-zip-result">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-navy">
                <b>{result.matched_count}</b> matched · <b>{result.skipped_count}</b> skipped · {result.total_files} file{result.total_files === 1 ? "" : "s"} scanned
              </div>
              <button type="button" onClick={() => setResult(null)} className="text-navy/50 hover:text-navy p-1" aria-label="Clear result">
                <X size={14} />
              </button>
            </div>
            {result.matched.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-olive mb-1.5">Attached</div>
                <ul className="text-xs divide-y divide-line border border-line rounded-sm">
                  {result.matched.map((m) => (
                    <li key={m.filename} data-testid={`bulk-zip-matched-${m.sku}`} className="px-3 py-2 flex items-center justify-between gap-3">
                      <span className="font-mono text-navy/60 truncate">{m.filename}</span>
                      <span className="text-navy">→ <b>{m.sku}</b> · {m.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.skipped.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-rose mb-1.5">Skipped</div>
                <ul className="text-xs divide-y divide-line border border-line rounded-sm">
                  {result.skipped.map((s, i) => (
                    <li key={`${s.filename}-${i}`} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`bulk-zip-skipped-${i}`}>
                      <span className="font-mono text-navy/60 truncate">{s.filename}</span>
                      <span className="text-rose/80">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
